import type { ReactNode } from 'react';

/**
 * English (default) translation dictionary and the source of truth for the
 * message shape — `pt` is type-checked against `typeof en` (see ./index).
 *
 * Plain strings cover static text; functions carry interpolation/pluralization
 * so the grammar stays inside the dictionary. A few entries return `ReactNode`
 * where translatable prose is interleaved with non-translatable bits (an email,
 * a `<code>` snippet) the caller supplies.
 */
// Display names for the kiosk pose codes offered by the snapshot-trigger's
// long-press chooser (config.triggerAnimationChoices). An unmapped code shows
// as its raw value rather than hiding the choice.
const poseNames: Record<string, string> = {
  'por/uma-mao': 'One hand',
  'por/bracos-cruzados': 'Arms crossed',
};

export const en = {
  common: {
    loading: 'Loading…',
    loadingSr: 'Loading',
    tryAgain: 'Try again',
    close: 'Close',
    today: 'Today',
    yesterday: 'Yesterday',
  },
  account: {
    label: 'Account',
    signedInAs: 'Signed in as',
    signOut: 'Sign out',
  },
  language: {
    label: 'Language',
    menuTitle: 'Change language',
    en: 'English',
    pt: 'Portuguese',
  },
  theme: {
    switchToLight: 'Switch to light mode',
    switchToDark: 'Switch to dark mode',
    lightTitle: 'Light mode',
    darkTitle: 'Dark mode',
  },
  day: {
    previous: 'Previous day',
    earliest: 'Oldest available day',
    next: 'Next day',
    select: 'Select day',
    today: 'Today',
    live: 'Live',
    liveTitle: 'New photos appear automatically',
    photoCount: (n: number): string => `${n} ${n === 1 ? 'photo' : 'photos'}`,
  },
  snapshot: {
    take: 'Take a snapshot',
    sending: 'Triggering…',
    sent: 'Snapshot triggered',
    error: 'Couldn’t reach the kiosk. Check the connection and try again.',
    denied: 'Permission denied. This database’s rules may not include the kiosk trigger yet.',
    choose: 'Choose the pose',
    chooseCancel: 'Cancel',
    pose: (code: string): string => poseNames[code] ?? code,
  },
  qrTrigger: {
    label: 'On-screen QR (phone trigger)',
    on: 'On',
    off: 'Off',
    error: 'Couldn’t save',
  },
  sound: {
    label: 'Kiosk sound',
    on: 'On',
    muted: 'Muted',
    volume: 'Volume',
    error: 'Couldn’t save',
  },
  kiosk: {
    settings: 'Kiosk settings',
    ready: 'Kiosk ready',
    offline: 'Kiosk offline',
    busy: 'Kiosk busy',
    gettingReady: 'Getting ready…',
    capturing: 'Capturing…',
    finishing: 'Finishing up…',
    errored: 'Kiosk error',
  },
  toolbar: {
    more: 'More actions',
  },
  photo: {
    unavailable: 'Image unavailable',
    alt: (time: string): string => `Snapshot taken at ${time}`,
    printAria: (time: string): string => `Print photo taken at ${time}`,
    print: 'Print',
    saveAria: (time: string): string => `Save photo taken at ${time}`,
    save: 'Save',
    saveError: 'Couldn’t save',
    saveRetry: 'Tap to save',
    deleteAria: (time: string): string => `Delete photo taken at ${time}`,
    delete: 'Delete',
  },
  gallery: {
    dialogLabel: 'Photo viewer',
    openAria: (time: string): string => `View photo taken at ${time} fullscreen`,
    counter: (n: number, total: number): string => `${n} of ${total}`,
    prev: 'Previous photo',
    next: 'Next photo',
  },
  selection: {
    enter: 'Select',
    enterAria: 'Select multiple photos to save',
    cancel: 'Cancel',
    selectedCount: (n: number): string => `${n} selected`,
    toggleAria: (time: string): string => `Select photo taken at ${time}`,
    save: (n: number): string =>
      n === 0 ? 'Save' : n === 1 ? 'Save 1 photo' : `Save ${n} photos`,
    saving: 'Saving…',
    retryHint: 'Ready — tap Save to finish.',
    error: 'Couldn’t save. Try again.',
    partial: (n: number): string =>
      `${n} ${n === 1 ? 'photo' : 'photos'} couldn’t be saved.`,
  },
  print: {
    dialogLabel: 'Print',
    confirmTitle: 'Print this photo?',
    confirmBody: (time: string): string => `Send the photo from ${time} to the printer.`,
    confirm: 'Print',
    cancel: 'Cancel',
    sending: 'Sending to the printer…',
    queued: 'Waiting for the printer…',
    printing: 'Printing…',
    printed: 'Sent to the printer ✓',
    printedTitle: 'Sent to the printer',
    printedBody: (time: string): string => `Your photo from ${time} is on its way to the printer.`,
    done: 'Done',
    backgroundHint: 'You can close this and keep working — it will keep printing.',
    failed: 'The printer reported a problem. Try again.',
    error: 'Couldn’t reach the printer service. Check the connection and try again.',
  },
  deleteDialog: {
    dialogLabel: 'Delete photo',
    title: 'Delete this photo?',
    body: 'This permanently removes the snapshot for everyone. It can’t be undone.',
    cancel: 'Cancel',
    confirm: 'Delete',
    deleting: 'Deleting…',
    error: 'Couldn’t delete the photo. Check the connection and try again.',
  },
  endOfFeed: {
    title: 'No more photos',
    sub: (n: number): string =>
      `You’ve reached the end — ${n} ${n === 1 ? 'photo' : 'photos'} for this day.`,
  },
  states: {
    loadingPhotos: 'Loading photos…',
    accessDeniedTitle: 'Access denied',
    accessDeniedMessage:
      'Your account isn’t allowed to read these photos. Sign out and sign in with an administrator account.',
    loadErrorTitle: 'Couldn’t load photos',
    loadErrorMessage:
      'There was a problem reaching the database. Check the connection — the grid will refresh automatically.',
    noPhotos: 'No photos for this day.',
    completingSignIn: 'Completing sign-in…',
    checkingAccess: 'Checking access…',
  },
  signIn: {
    leadConfirm: 'Confirm the email address this sign-in link was sent to.',
    leadDefault:
      'Sign in with your email — we’ll send you a secure sign-in link. No password needed.',
    emailLabel: 'Email address',
    working: 'Working…',
    complete: 'Complete sign-in',
    send: 'Send sign-in link',
    startOver: 'Start over',
    note: 'Access is limited to museum administrators.',
  },
  notAuthorized: {
    heading: 'Not authorized',
    leadWithEmail: (email: ReactNode): ReactNode => (
      <>
        The account <strong>{email}</strong> isn’t a museum administrator.
      </>
    ),
    leadNoEmail: 'This account isn’t a museum administrator.',
    note: 'Backoffice access requires an admin role. Ask an administrator to grant access to your account, then sign in again.',
    useDifferent: 'Use a different account',
  },
  config: {
    heading: 'Configuration required',
    missingIntro: 'The app can’t start because these environment variables aren’t set:',
    missingHelp: (example: ReactNode, local: ReactNode, apiKey: ReactNode, appId: ReactNode): ReactNode => (
      <>
        Copy {example} to {local}, fill in the values (the {apiKey} and {appId} come from the
        Firebase console → Project settings → your Web app), then restart the dev server. See the
        README for details.
      </>
    ),
    initFailed: 'The app failed to initialize Firebase.',
    initHelp: (vitePrefix: ReactNode, local: ReactNode, dbUrl: ReactNode): ReactNode => (
      <>
        Check that the {vitePrefix} values in {local} are correct (especially {dbUrl}), then
        restart the dev server.
      </>
    ),
  },
  auth: {
    yourAccount: 'your account',
    linkSent: (email: string): string =>
      `We sent a sign-in link to ${email}. Open it on this device to continue.`,
    errors: {
      invalidEmail: 'That doesn’t look like a valid email address.',
      missingEmail: 'Please enter your email address.',
      expiredLink: 'This sign-in link has expired or was already used. Request a new one.',
      unauthorizedDomain:
        'This app’s domain isn’t authorized for sign-in. Add it under Firebase Auth → Authorized domains.',
      network: 'Network error. Check the connection and try again.',
      tooManyRequests: 'Too many attempts. Please wait a moment and try again.',
      generic: 'Something went wrong. Please try again.',
    },
  },
};
